import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Copy, Send, Loader2, Linkedin, Twitter, Facebook, MessageCircle, UserPlus, Gift, Wallet, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("auth_token")}` });
const TONE = { rewarded: "bg-emerald-50 text-emerald-700 border-emerald-200", pending: "bg-amber-50 text-amber-700 border-amber-200", waiting_credit: "bg-amber-50 text-amber-700 border-amber-200", rejected: "bg-slate-50 text-slate-600 border-slate-200", capped: "bg-slate-50 text-slate-600 border-slate-200" };
const fmtDate = (iso) => new Date(iso).toLocaleDateString("de-DE");

export function ReferralSection() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/referrals/me`, { headers: authHeaders() }).then(r => setData(r.data)).catch(() => toast.error("Empfehlungen konnten nicht geladen werden"));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>;
  const text = encodeURIComponent("Ich nutze WebGenerator für meine Firmenwebseite – in 2 Minuten online:");
  const url = encodeURIComponent(data.link);
  const shares = [
    { id: "linkedin", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`, cls: "bg-[#0A66C2]" },
    { id: "x", icon: Twitter, href: `https://twitter.com/intent/tweet?text=${text}&url=${url}`, cls: "bg-black" },
    { id: "facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${url}`, cls: "bg-[#1877F2]" },
    { id: "whatsapp", icon: MessageCircle, href: `https://wa.me/?text=${text}%20${url}`, cls: "bg-[#25D366]" },
  ];

  const copy = async () => {
    try { await navigator.clipboard.writeText(data.link); toast.success("Link kopiert"); } catch { toast.error("Kopieren nicht möglich"); }
  };
  const invite = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/referrals/invite`, { email }, { headers: authHeaders() });
      toast.success("Einladung gesendet"); setEmail(""); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Bitte gib eine gültige E-Mail-Adresse ein.");
    } finally { setSending(false); }
  };

  const { stats } = data;
  return (
    <div className="max-w-4xl min-w-0" data-testid="referral-section">
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Lade Freunde ein. Erhalte Guthaben.</h2>
      <p className="text-sm text-slate-600 mt-2 mb-8">Erhalte bis zu {data.cap_eur} € Guthaben, indem du WebGenerator weiterempfiehlst.</p>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6">
        <div className="space-y-6 min-w-0">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 sm:p-6">
            <h3 className="font-semibold text-slate-900 mb-3">Teile deinen Empfehlungslink</h3>
            <div className="flex items-center gap-2 bg-slate-50 rounded-xl p-2 pl-4">
              <span className="flex-1 min-w-0 truncate text-sm text-slate-700" data-testid="referral-link">{data.link}</span>
              <Button onClick={copy} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="referral-copy-btn"><Copy className="w-4 h-4 mr-1.5" />Kopieren</Button>
            </div>
            <div className="flex gap-2 mt-4">
              {shares.map(({ id, icon: Icon, href, cls }) => (
                <a key={id} href={href} target="_blank" rel="noopener noreferrer" aria-label={id} data-testid={`referral-share-${id}`}
                  className={`${cls} w-10 h-10 rounded-lg flex items-center justify-center text-white hover:opacity-90 transition-opacity`}><Icon className="w-5 h-5" /></a>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 sm:p-6">
            <h3 className="font-semibold text-slate-900 mb-3">Einladen via E-Mail</h3>
            <div className="flex gap-2">
              <Input type="email" placeholder="freund@beispiel.de" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && email && invite()} data-testid="referral-email-input" />
              <Button onClick={invite} disabled={sending || !email} variant="outline" className="rounded-lg" data-testid="referral-invite-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" />Senden</>}
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 sm:p-6 self-start" data-testid="referral-howto">
          <h3 className="font-semibold text-slate-900 mb-4">Empfehle weiter, erhalte {data.reward_eur} €</h3>
          <ul className="space-y-4 text-sm text-slate-700">
            <li className="flex gap-3"><span className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0"><UserPlus className="w-4 h-4 text-indigo-600" /></span><span><strong>Lade Freunde</strong> mit deinem Empfehlungslink zu WebGenerator ein.</span></li>
            <li className="flex gap-3"><span className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0"><Gift className="w-4 h-4 text-amber-600" /></span><span>Sie schließen ein <strong>Abo</strong> ab und bezahlen erfolgreich.</span></li>
            <li className="flex gap-3"><span className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0"><Wallet className="w-4 h-4 text-emerald-600" /></span><span><strong>Du erhältst {data.reward_eur} € Guthaben</strong> für jeden Freund – nach {data.wait_days} Tagen. Es wird automatisch mit deinen Abo-Rechnungen verrechnet.</span></li>
          </ul>
          <p className="text-xs text-slate-500 mt-4">Du kannst bis zu {data.cap_eur} € Guthaben erhalten.</p>
        </div>
      </div>

      <h3 className="text-lg font-semibold text-slate-900 mt-10">Eingeladene Freunde</h3>
      <p className="text-sm text-slate-500 mb-4">Verfolge den Status und die Aktivität deiner Empfehlungen.</p>
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 flex items-center justify-between"><div><p className="text-2xl font-bold" data-testid="referral-stat-invites">{stats.invites_sent}</p><p className="text-sm text-slate-500">Einladungen per E-Mail</p></div><Users className="w-8 h-8 text-slate-200" /></div>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 flex items-center justify-between"><div><p className="text-2xl font-bold" data-testid="referral-stat-subscribed">{stats.subscribed}</p><p className="text-sm text-slate-500">Freunde haben abonniert</p></div><Gift className="w-8 h-8 text-slate-200" /></div>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 flex items-center justify-between"><div><p className="text-2xl font-bold" data-testid="referral-stat-credit">{stats.credit_eur} € <span className="text-sm font-normal text-slate-400">/ {data.cap_eur} €</span></p><p className="text-sm text-slate-500">Erzieltes Guthaben</p></div><Wallet className="w-8 h-8 text-slate-200" /></div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mt-4 overflow-hidden" data-testid="referral-friends">
        {data.friends.length === 0 ? (
          <p className="text-center text-sm text-slate-400 py-10" data-testid="referral-empty">Noch keine Empfehlungen. Teile deinen Link, um zu starten.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-slate-500 border-b border-slate-100"><tr><th className="px-4 py-3">Freund</th><th className="px-4 py-3">Datum</th><th className="px-4 py-3 text-right">Status</th></tr></thead>
            <tbody>{data.friends.map((f, i) => (
              <tr key={i} className="border-b border-slate-50 last:border-0" data-testid={`referral-friend-${i}`}>
                <td className="px-4 py-3 text-slate-700">{f.email}</td><td className="px-4 py-3 text-slate-500">{fmtDate(f.date)}</td>
                <td className="px-4 py-3 text-right"><span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${TONE[f.status] || TONE.rejected}`}>{f.label}{f.amount_eur ? ` · +${f.amount_eur} €` : ""}</span></td>
              </tr>))}</tbody>
          </table>
        )}
      </div>

      <details className="mt-8 text-xs text-slate-500" data-testid="referral-terms">
        <summary className="cursor-pointer font-medium text-slate-600">Teilnahmebedingungen</summary>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Du erhältst {data.reward_eur} € Guthaben, wenn eine von dir geworbene Person als Neukunde ein kostenpflichtiges Abo abschließt und erfolgreich bezahlt.</li>
          <li>Die Gutschrift erfolgt nach {data.wait_days} Tagen, sofern das Abo dann noch aktiv ist (Schutz vor Widerruf und Rückbuchung).</li>
          <li>Das Guthaben wird mit künftigen Abo-Rechnungen verrechnet, eine Barauszahlung ist ausgeschlossen. Maximal {data.cap_eur} € pro Kunde.</li>
          <li>Selbstempfehlungen und Mehrfachkonten sind ausgeschlossen. Bei Missbrauch kann das Guthaben aberkannt werden.</li>
          <li>Eine Einladung per E-Mail darf nur an Personen gesendet werden, die damit einverstanden sind (kein Spam).</li>
        </ul>
      </details>
    </div>
  );
}
